/**
 * Bannerstudio: the full-page banner editor (owner 2026-10-31: "a proper editing section to make banners"). From
 * «+ Nytt banner» (#/bannere/ny), «Endre» on a draft (#/bannere/<id>), «Lag en kopi» (#/bannere/ny?fra=<id>) and «Lag
 * banner fra arrangementet» (#/bannere/ny?arrangement=<id>).
 *
 * Still a constrained editor, not a canvas (CA-40): staff pick a design, write the text, choose colours and an
 * ornament — nobody moves a layer or picks a font, so every banner from the mosque has one look and reads from across
 * a corridor. What is new: the whole page for it; the preview in the three shapes a banner hangs in (a whole TV, the
 * half beside the times, the app's card); «Pynt» — any ornament on any text design, or none; «Bilde med tekst» — the
 * mosque's own photo under the text; «Last ned» as a 1920×1080 picture; and a banner made from a program, laid on
 * that program when it is saved.
 *
 * The preview is the API's own render() — the same layer sizes the TV uses — asked for on every keystroke (debounced),
 * so what is typed here is what hangs in the corridor. The ornaments are drawn by core in the browser (BannerFace).
 * Everything saved here is a DRAFT (docs/platform-implementation/BANNER_LIFECYCLE.md): «Publiser» is in the list.
 */
import React, { useEffect, useRef, useState } from 'react';
import { blobUrl, errText, patch, post, saveBlob, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty, useNav } from '../lib/nav';
import { longDate, WEEKDAY_LONG } from '../lib/format';
import { Btn, DropZone, Field, LoadError, Loading, Msg, Tag } from '../components/ui';
import { BannerCore, Social } from '../core';
import { BannerFace, StaffPicture, type FaceShape } from '../components/BannerFace';
import { readPicture } from './Butikk';
import { attachToEvent, bannerTitle, sourceOf } from '../lib/banner-attach';
import { drawScreenImage, mosquePublic } from '../lib/social';

export const LAYER_LABEL: Record<string, string> = { arabic: 'Arabisk tekst', eyebrow: 'Merkelapp', headline: 'Overskrift', detail: 'Tekst', when: 'Når', where: 'Hvor' };
const PIC_MAX = 8 * 1024 * 1024, PIC_MIN_SIDE = 400;
const APP_NOTE = (days: number) => `Vises i medlemsappen i tillegg til skjermene, når banneret er publisert. Bare publisert tekst vises i appen; høyst ${days} dager.`;
export const SAVED = 'Lagret som utkast. Trykk «Publiser» i listen når det er klart, så vises det.';
/** Where the list shows what the studio did after it has navigated back (one message, read once). */
export const FLASH_KEY = 'masjid.banners.flash';
type Kind = 'text' | 'photo' | 'image';
const KIND_NO: Record<Kind, string> = { text: 'Tekst på en mal', photo: 'Bilde med tekst', image: 'Eget bilde' };
const SHAPES: { id: FaceShape; label: string; note: string }[] = [
  { id: 'wide', label: 'Hel skjerm', note: 'En hel TV (16:9).' },
  { id: 'split', label: 'Delt skjerm', note: 'Halvdelen ved siden av bønnetidene.' },
  { id: 'phone', label: 'Telefon', note: 'Kortet på appens forside (når «Vis også i appen» er valgt).' },
];

/** A stored time back into a datetime-local field (Oslo wall clock). */
export function toInput(v: string | null | undefined): string {
  if (!v) return '';
  if (!/(Z|[+-]\d\d:\d\d)$/.test(v)) return v.slice(0, 16);
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Oslo', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(v)).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** The «Når» line for a program's banner: «Lørdag 24. oktober 2026 kl. 18:00», from its wall-clock start. */
export function whenLine(startsAt: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(startsAt || ''));
  if (!m) return '';
  const day = WEEKDAY_LONG[new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay()];
  return `${day.charAt(0).toUpperCase()}${day.slice(1)} ${longDate(startsAt)} kl. ${m[4]}:${m[5]}`;
}

/** A saved banner's picture through the staff route, as the editor holds one (a copy of a photo banner brings its photo along). */
async function staffPictureData(id: string): Promise<{ data: string; w: number; h: number; bytes: number } | null> {
  const url = await blobUrl(`/admin/banners/${encodeURIComponent(id)}/image`);
  if (!url) return null;
  try {
    const blob = await (await fetch(url)).blob();
    const data = await new Promise<string>((resolve, reject) => { const r = new FileReader(); r.onerror = () => reject(r.error); r.onload = () => resolve(String(r.result || '')); r.readAsDataURL(blob); });
    const im = await new Promise<HTMLImageElement>((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = () => reject(new Error('image')); i.src = data; });
    return { data, w: im.naturalWidth, h: im.naturalHeight, bytes: blob.size };
  } catch { return null; } finally { URL.revokeObjectURL(url); }
}

/** Dates and the member-app choice: the same three fields in every form. */
function Reach({ from, to, inApp, setFrom, setTo, setInApp, maxAppDays }: {
  from: string; to: string; inApp: boolean; setFrom: (v: string) => void; setTo: (v: string) => void; setInApp: (v: boolean) => void; maxAppDays: number;
}) {
  return (
    <>
      <div className="row">
        <Field label="Vises fra (valgfritt)" className="field half">{(id) => <input id={id} type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />}</Field>
        <Field label="Vises til (valgfritt)" className="field half" note="Uten sluttdato fjernes banneret automatisk 14 dager etter at det er publisert.">{(id, d) =>
          <input id={id} aria-describedby={d} type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />}</Field>
      </div>
      <label className="work-check"><input type="checkbox" aria-describedby="bnr-app-note" checked={inApp} onChange={(e) => setInApp(e.target.checked)} /> Vis også i appen</label>
      <p className="field note" id="bnr-app-note" style={{ marginTop: 0 }}>{APP_NOTE(maxAppDays)}</p>
    </>
  );
}

/** The design gallery: each template drawn by core in its own palette with its starting headline. The photo design is its own card. */
function TemplateGallery({ ids, value, onPick }: { ids: string[]; value: string; onPick: (id: string) => void }) {
  return (
    <div className="bnr-gallery" role="radiogroup" aria-label="Design">
      {ids.map((id) => {
        const t = BannerCore.TEMPLATES[id];
        if (!t) return null;
        const r = BannerCore.render(id, { headline: t.starter || t.name_no, ...(t.arabic ? { arabic: t.arabic } : {}) }, { palette: t.palette || 'green' });
        if (!r) return null;
        return (
          <button key={id} type="button" role="radio" aria-checked={value === id} className="bnr-tpl" onClick={() => onPick(id)}>
            <BannerFace r={r} template={id} palette={t.palette || 'green'} thumb />
            <span className="bnr-tpl-name">{t.name_no}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The palette swatches: each palette as its ground with its text colour, named. */
function PaletteSwatches({ palettes, value, onPick }: { palettes: Record<string, Body>; value: string; onPick: (id: string) => void }) {
  return (
    <div className="bnr-swatches" role="radiogroup" aria-label="Farger">
      {Object.entries(palettes).map(([k, p]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} className="bnr-swatch" onClick={() => onPick(k)} title={p.name_no}>
          <span className="bnr-swatch-chip" style={{ background: p.bg, color: p.fg, borderColor: p.accent }} aria-hidden="true">Aa</span>
          <span className="bnr-swatch-name">{p.name_no}</span>
        </button>
      ))}
    </div>
  );
}

/** «Pynt»: the template's own ornament, none, or any of core's — each drawn small in the chosen palette. */
function OrnamentPicker({ template, palette, value, onPick }: { template: string; palette: string; value: string | null; onPick: (v: string | null) => void }) {
  const tpl = BannerCore.TEMPLATES[template];
  const own = tpl && tpl.decor ? tpl.decor : null;
  const ownName = own ? (BannerCore.ORNAMENTS.find((o) => o.id === own) || { name_no: own }).name_no : 'ingen';
  const choices: { id: string | null; name: string }[] = [{ id: null, name: `Malens egen (${ownName})` }, { id: 'none', name: 'Ingen pynt' },
    ...BannerCore.ORNAMENTS.map((o) => ({ id: o.id, name: o.name_no }))];
  return (
    <div className="bnr-gallery bnr-ornaments" role="radiogroup" aria-label="Pynt">
      {choices.map((c) => {
        const r = BannerCore.render(template, { headline: 'Aa' }, { palette, decor: c.id });
        if (!r) return null;
        return (
          <button key={String(c.id)} type="button" role="radio" aria-checked={value === c.id} className="bnr-tpl" onClick={() => onPick(c.id)}>
            <BannerFace r={{ ...r, layers: [] }} template={template} palette={palette} decor={c.id} thumb />
            <span className="bnr-tpl-name">{c.name}</span>
          </button>
        );
      })}
    </div>
  );
}

export function BannerStudio({ id, query }: { id: string | null; query: URLSearchParams }) {
  const { data, error, reload } = useLoad<Body>('/banners');
  const { caps, refreshBadges } = useNav();
  const fromId = query.get('fra');
  const eventId = query.get('arrangement');
  // The program this banner is for (prefilled from it, laid on it when saved), only when asked for and it exists.
  const events = useLoad<Body>(eventId ? '/events' : null);
  const event: Body | null = eventId && events.data ? ((events.data.items || []).find((e: Body) => e.id === eventId) || null) : null;

  const [kind, setKind] = useState<Kind>('text');
  const [tpl, setTpl] = useState(BannerCore.TEMPLATE_ORDER.find((t) => !BannerCore.TEMPLATES[t].photo) || 'greeting');
  const [pal, setPal] = useState('green');
  const [decor, setDecor] = useState<string | null>(null);
  const [layers, setLayers] = useState<Record<string, string>>({});
  const [name, setName] = useState('');
  const [pic, setPic] = useState<{ data: string; w: number; h: number; bytes: number } | null>(null);
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [inApp, setInApp] = useState(false);
  const [render, setRender] = useState<Body | null>(null);
  const [warn, setWarn] = useState<{ text: string; tone: '' | 'err' }>({ text: '', tone: '' });
  const [shape, setShape] = useState<FaceShape>('wide');
  // UX audit U-18 (2026-10-19): what is missing is said once a field has been typed in, or «Lagre» was pressed.
  const [touched, setTouched] = useState(false);
  const [msg, say] = useMsg();
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const items: Body[] = (data && data.items) || [];
  const editing: Body | null = id ? (items.find((b) => b.id === id) || null) : null;
  const palettes: Record<string, Body> = (data && data.palettes) || BannerCore.PALETTES;
  const maxAppDays: number = (data && data.maxAppDays) || 31;
  const template = BannerCore.TEMPLATES[kind === 'photo' ? 'photo' : tpl] || BannerCore.TEMPLATES.greeting;
  const typed = Boolean(pic) || Object.values(layers).some((v) => v.trim()) || Boolean(name.trim());
  useDirty('banner-studio', typed && !saving);

  /** Loads the draft being edited, or the banner being copied, or the program's words — once the list has come. */
  useEffect(() => {
    if (!data) return;
    const key = `${id || ''}|${fromId || ''}|${eventId || ''}|${event ? event.id : ''}`;
    if (loaded === key) return;
    const src = editing || (fromId ? items.find((b) => b.id === fromId) : null);
    if (src) {
      const k: Kind = src.template === 'image' ? 'image' : src.template === 'photo' ? 'photo' : 'text';
      setKind(k);
      if (k === 'text') { setTpl(src.template); setDecor(src.decor || null); }
      setPal(src.palette || 'green');
      if (k === 'image') setName((src.layers && src.layers.headline) || ''); else setLayers({ ...(src.layers || {}) });
      if (editing) { setFrom(toInput(src.startsAt)); setTo(toInput(src.endsAt)); setInApp(Boolean(src.inApp)); }
      // A copy of a photo or picture banner: its picture comes along (review 2026-10-31), so the copy is complete.
      if (!editing && k !== 'text') void staffPictureData(src.id).then((p) => { if (p) setPic(p); else say('Bildet kunne ikke hentes. Velg det på nytt.', 'err'); });
      setLoaded(key); return;
    }
    if (eventId && !event) { if (events.data) setLoaded(key); return; }
    if (event) {
      // A banner for a program: the event design with its title, when and where — all still editable here.
      setKind('text'); setTpl('skyline'); setPal(BannerCore.TEMPLATES.skyline.palette || 'navy'); setDecor(null);
      const head = BannerCore.TEMPLATES.skyline.layers.find((l) => l.id === 'headline');
      setLayers({ headline: String(event.title_no || '').slice(0, head ? head.maxChars * (head.lines || 1) : 34), when: whenLine(event.starts_at), where: event.location_no || '' });
      setFrom(''); setTo(event.starts_at ? String(event.starts_at).slice(0, 16) : '');
    }
    setLoaded(key);
  }, [data, event, events.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only the current template's layers are sent: text typed into a layer the new template does not have stays in the
  // form, but is not the banner.
  const current = (): Record<string, string> => Object.fromEntries((template ? template.layers : []).map((l) => [l.id, layers[l.id] || '']));

  useEffect(() => {
    if (kind === 'image') { setRender(null); setWarn({ text: '', tone: '' }); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const r = await post('/banners/preview', { template: template.id, layers: current(), palette: pal, decor: kind === 'text' ? decor : null });
      if (!r.ok || !r.body.render) { setWarn({ text: errText(r.body), tone: 'err' }); return; }
      setRender(r.body);
      const rd = r.body.render;
      const notes: string[] = [];
      if (rd.anyUnreadable) notes.push('Noe av teksten blir for liten til å leses på avstand. Kort den ned.');
      else if (rd.anyShrunk) notes.push('Teksten er gjort litt mindre for å få plass.');
      const missing = Object.keys(r.body.errors || {});
      const said = touched ? missing : [];
      if (said.length) notes.push('Mangler: ' + said.map((k) => LAYER_LABEL[k] || k).join(', ') + '.');
      if (r.body.contrast && r.body.contrast.ok === false) notes.push('Fargene har for lav kontrast.');
      if (kind === 'photo' && !pic && !editing) notes.push('Velg et bilde.');
      setWarn({ text: notes.join(' '), tone: rd.anyUnreadable || said.length ? 'err' : '' });
    }, 220);
    return () => clearTimeout(timer.current);
  }, [template && template.id, pal, kind, decor, touched, Boolean(pic), JSON.stringify(layers)]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  if (id && !editing) return <div className="card"><div className="card-body">Fant ikke utkastet. <a href="#/bannere">Til listen</a>.</div></div>;
  if (editing && editing.state !== 'draft') return <div className="card"><div className="card-body">Bare et utkast kan endres. Ta banneret tilbake til utkast i listen først. <a href="#/bannere">Til listen</a>.</div></div>;

  /** A fresh form (after a save the page goes back to the list; this keeps the state honest meanwhile). */
  function stopEdit() { setLayers({}); setName(''); setPic(null); setFrom(''); setTo(''); setInApp(false); setTouched(false); }
  /** Choosing a design: its own palette, and its headline to start from when the headline is still empty. */
  function pickTemplate(tid: string) {
    setTpl(tid); setDecor(null);
    const t = BannerCore.TEMPLATES[tid];
    if (!t) return;
    if (t.palette && palettes[t.palette]) setPal(t.palette);
    const next = { ...layers };
    if (t.starter && !(layers.headline || '').trim()) next.headline = t.starter;
    if (t.arabic && !(layers.arabic || '').trim()) next.arabic = t.arabic;
    if (next.headline !== layers.headline || next.arabic !== layers.arabic) setLayers(next);
  }
  function pickKind(k: Kind) {
    setKind(k);
    if (k === 'photo') setPal((p) => (p === 'green' ? 'dark' : p));
  }
  function choose(f: File | undefined) {
    say('');
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) return say('Bildet må være JPG, PNG eller WebP.', 'err');
    if (f.size > PIC_MAX) return say('Bildet er for stort. Maks 8 MB.', 'err');
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = String(r.result || '');
      const im = new Image();
      im.onload = async () => {
        if (Math.min(im.naturalWidth, im.naturalHeight) < PIC_MIN_SIDE) {
          setPic(null);
          return say(`Bildet er for lite for en TV (${im.naturalWidth}×${im.naturalHeight}). Den korteste siden må være minst ${PIC_MIN_SIDE} piksler.`, 'err');
        }
        // A photo under text: a large phone picture is made smaller in the browser first (Butikk readPicture), as a
        // product picture is; a picture banner is sent as it is, whole.
        if (kind === 'photo') {
          const rp = await readPicture(f);
          if (!rp.ok) return say(rp.message, 'err');
          setPic({ data: rp.pic.data, w: rp.pic.w, h: rp.pic.h, bytes: rp.pic.bytes });
        } else {
          setPic({ data: dataUrl, w: im.naturalWidth, h: im.naturalHeight, bytes: f.size });
          if (!name.trim()) setName(f.name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').slice(0, 80));
        }
      };
      im.onerror = () => say('Bildet kunne ikke leses. Lagre det på nytt som JPG eller PNG.', 'err');
      im.src = dataUrl;
    };
    r.readAsDataURL(f);
  }

  /** What is on the page now, as the drawing and the attach need it. */
  const draft = (bid: string): Body => ({ id: bid, template: kind === 'image' ? 'image' : template.id, palette: pal, decor: kind === 'text' ? decor : null,
    layers: kind === 'image' ? { headline: name.trim() } : current() });

  async function downloadPicture() {
    say('');
    const m = await mosquePublic();
    const src = sourceOf(draft(editing ? editing.id : 'ny'), pic ? pic.data : null);
    if ((kind === 'image' || kind === 'photo') && !pic && !editing) return say('Velg et bilde først.', 'err');
    const blob = await drawScreenImage(src, m.shortName, 'image/png');
    if (!blob) return say('Bildet kunne ikke lages i denne nettleseren.', 'err');
    saveBlob(blob, Social.fileName(bannerTitle(draft('ny')), 'square', 'banner').replace(/-\d+x\d+\.jpg$/, '-1920x1080.png'));
  }

  async function save() {
    // QA C (2026-10-16): a second click while the first save ran made a second draft.
    if (saving) return;
    setTouched(true);
    if (kind === 'image' && !editing && !pic) return say('Velg et bilde.', 'err');
    if (kind === 'photo' && !editing && !pic) return say('Velg et bilde.', 'err');
    setSaving(true);
    try {
      const reach = { startsAt: from || null, endsAt: to || null, inApp };
      const fields = kind === 'image'
        ? { layers: { headline: name.trim() }, ...reach, ...(pic ? { image: { data: pic.data } } : {}) }
        : { palette: pal, layers: current(), ...reach, ...(kind === 'text' ? { decor } : {}), ...(pic && kind === 'photo' ? { image: { data: pic.data } } : {}) };
      const r = editing ? await patch(`/banners/${encodeURIComponent(editing.id)}`, fields)
        : await post('/banners', { template: kind === 'image' ? 'image' : template.id, ...fields });
      if (!r.ok) { say(errText(r.body, LAYER_LABEL), 'err'); return; }
      const bid: string = editing ? editing.id : r.body.id;
      let text = editing ? 'Endringen er lagret.' : SAVED;
      if (event) {
        // Made from a program: laid on it now, drawn from what was just saved.
        const bad = await attachToEvent(event.id, draft(bid), pic ? pic.data : null);
        text += bad ? ` Banneret ble ikke lagt på arrangementet: ${bad}` : ` Lagt på arrangementet «${event.title_no}» som bilde.`;
      }
      try { sessionStorage.setItem(FLASH_KEY, JSON.stringify({ text, tone: event && /ikke lagt/.test(text) ? 'err' : 'ok' })); } catch { /* the list says nothing, the save still happened */ }
      refreshBadges();
      stopEdit();
      location.hash = '/bannere';
    } finally { setSaving(false); }
  }

  const rd = render && render.render;
  const fit = (render && render.fit) || {};
  const face = kind === 'image'
    ? null
    : <BannerFace r={rd ? { ...rd, photo: kind === 'photo' } : { palette: { bg: palettes[pal]?.bg || 'var(--green)', fg: palettes[pal]?.fg || 'var(--white)' }, layers: [], photo: kind === 'photo' }}
        template={template.id} palette={pal} decor={kind === 'text' ? decor : null} shape={shape}
        photoSrc={pic ? pic.data : null} photoId={!pic && editing && kind === 'photo' ? editing.id : null} label="Forhåndsvisning av banneret" />;
  const title = editing ? `Endre utkast: ${bannerTitle(editing)}` : event ? `Nytt banner for «${event.title_no}»` : fromId ? 'Kopi av banner' : 'Nytt banner';
  const canPhoto = Boolean(caps && caps.includes('banners'));

  return (
    <div className="studio">
      <div className="studio-top">
        <a className="studio-back" href="#/bannere">← Bannere</a>
        <h2>{title}</h2>
        {editing && <Tag tone="grey">utkast</Tag>}
        <div className="sp" />
        <Btn ghost onClick={() => void downloadPicture()} aria-label="Last ned banneret som bilde (1920×1080)">Last ned</Btn>
        <Btn disabled={saving} aria-busy={saving || undefined} onClick={save}>{saving ? 'Lagrer …' : editing ? 'Lagre endringen' : 'Lagre som utkast'}</Btn>
      </div>
      <p className="note studio-hint">
        {kind === 'image' ? 'Last opp en plakat dere allerede har. Et bilde oversettes ikke — bruk en tekstmal når budskapet skal leses på flere språk.'
          : kind === 'photo' ? 'Et bilde dere har, med tekst over. Teksten oversettes til engelsk og urdu; bildet mørkes litt så teksten kan leses.'
          : 'Velg design og fyll inn tekst. Tekst som er for lang blir mindre, ikke kuttet — og du ser det her før det henger på veggen. Teksten oversettes til engelsk og urdu.'}
        {' '}Alt som lagres her er et utkast til du publiserer det i listen.
      </p>
      <div className="studio-body">
        <div className="studio-side">
          <section className="studio-step" aria-labelledby="st-design">
            <h3 id="st-design"><span className="studio-n">1</span> Design</h3>
            {!editing && (
              <div className="bnr-seg" role="group" aria-label="Type banner">
                {(['text', 'photo', 'image'] as Kind[]).filter((k) => k !== 'photo' || canPhoto).map((k) => (
                  <button key={k} type="button" aria-pressed={kind === k} onClick={() => pickKind(k)}>{KIND_NO[k]}</button>))}
              </div>
            )}
            {editing && <p className="field note" style={{ marginTop: 0 }}>{KIND_NO[kind]}{kind === 'text' ? <>: <b>{template.name_no}</b> (designet kan ikke byttes på et lagret utkast — lag en kopi for et annet)</> : ''}.</p>}
            {kind === 'text' && !editing && <TemplateGallery ids={BannerCore.TEMPLATE_ORDER.filter((t) => !BannerCore.TEMPLATES[t].photo)} value={template.id} onPick={pickTemplate} />}
            {(kind === 'photo' || kind === 'image') && (
              <>
                <DropZone title={pic ? 'Bytt bilde' : editing ? 'Bytt bildet' : 'Velg et bilde'} accept="image/jpeg,image/png,image/webp" onFile={choose}>
                  <span className="note">{kind === 'photo' ? 'JPG, PNG eller WebP. Helst liggende (16:9), f.eks. 1920×1080; et stort bilde gjøres mindre her.'
                    : 'JPG, PNG eller WebP, høyst 8 MB. Bildet vises helt, aldri beskåret. Til delt visning passer et stående bilde (f.eks. 1080×1350); til hel skjerm et liggende (1920×1080).'}</span>
                </DropZone>
                {pic && <p className="field note" style={{ marginTop: 6 }}>{pic.w}×{pic.h} piksler · {(pic.bytes / 1048576).toFixed(1)} MB.</p>}
              </>
            )}
          </section>
          <section className="studio-step" aria-labelledby="st-text">
            <h3 id="st-text"><span className="studio-n">2</span> Tekst</h3>
            {kind === 'image' ? (
              <Field label="Bildebeskrivelse" required note="Leses opp for dem som bruker skjermleser, på skjermene og i appen, og vises i listen. Beskriv det bildet sier — f.eks. «Plakat: Eid-bønn lørdag kl. 08:30».">{(fid, d) =>
                <input id={fid} aria-describedby={d} value={name} maxLength={80} placeholder="F.eks. Plakat: Eid-bønn lørdag kl. 08:30" onChange={(e) => { setTouched(true); setName(e.target.value); }} />}</Field>
            ) : template.layers.map((l) => {
              const budget = l.maxChars * (l.lines || 1);
              const f = fit[l.id];
              const n = (layers[l.id] || '').trim().length;
              const over = f && f.budget ? f.fits === false : n > budget;
              return (
                <Field key={l.id} required={l.required} label={l.options ? (LAYER_LABEL[l.id] || l.id) : <>{LAYER_LABEL[l.id] || l.id}<span className={'count' + (over ? ' over' : '')} aria-live="polite">{f && f.budget ? `${f.chars} / ${f.budget}` : `${n} / ${budget}`}</span></>}
                       note={l.options ? 'Velges fra listen — oversettes ikke.' : undefined}>
                  {(fid) => l.options
                    ? <select id={fid} value={layers[l.id] || ''} onChange={(e) => { setTouched(true); setLayers({ ...layers, [l.id]: e.target.value }); }}>
                        <option value="">Ingen</option>
                        {(l.options as string[]).map((k) => <option key={k} value={k}>{BannerCore.ARABIC[k] ? `${BannerCore.ARABIC[k].name_no} — ${BannerCore.ARABIC[k].ar}` : k}</option>)}
                      </select>
                    : (l.lines || 1) > 1
                    ? <textarea id={fid} rows={2} placeholder={l.placeholder_no} value={layers[l.id] || ''} onChange={(e) => { setTouched(true); setLayers({ ...layers, [l.id]: e.target.value }); }} />
                    : <input id={fid} placeholder={l.placeholder_no || (l.id === 'headline' && template.starter ? 'F.eks. ' + template.starter : undefined)} value={layers[l.id] || ''} onChange={(e) => { setTouched(true); setLayers({ ...layers, [l.id]: e.target.value }); }} />}
                </Field>
              );
            })}
          </section>
          {kind !== 'image' && (
            <section className="studio-step" aria-labelledby="st-look">
              <h3 id="st-look"><span className="studio-n">3</span> Farger{kind === 'text' ? ' og pynt' : ''}</h3>
              <PaletteSwatches palettes={palettes} value={pal} onPick={setPal} />
              {kind === 'text' && <OrnamentPicker template={template.id} palette={pal} value={decor} onPick={setDecor} />}
              {kind === 'photo' && <p className="field note">Fargene gir tekstfargen og tonen bildet mørkes med.</p>}
            </section>
          )}
          <section className="studio-step" aria-labelledby="st-reach">
            <h3 id="st-reach"><span className="studio-n">{kind === 'image' ? 3 : 4}</span> Visning</h3>
            <Reach from={from} to={to} inApp={inApp} setFrom={setFrom} setTo={setTo} setInApp={setInApp} maxAppDays={maxAppDays} />
            {event && <p className="field note">Når du lagrer, legges banneret også på arrangementet «{event.title_no}» som bilde (i appen, på skjermene og på nettsiden).</p>}
            {eventId && !event && events.data && <p className="msg warn" role="status">Fant ikke arrangementet. Banneret lagres som vanlig.</p>}
          </section>
          <div className="row studio-acts">
            <a className="btn sm ghost" href="#/bannere">Avbryt</a>
            <Btn disabled={saving} aria-busy={saving || undefined} onClick={save}>{saving ? 'Lagrer …' : editing ? 'Lagre endringen' : 'Lagre som utkast'}</Btn>
          </div>
          <Msg text={msg.text} tone={msg.tone} />
        </div>
        <div className="studio-preview">
          <div className="bnr-seg" role="group" aria-label="Forhåndsvisning som">
            {SHAPES.map((s) => <button key={s.id} type="button" aria-pressed={shape === s.id} onClick={() => setShape(s.id)}>{s.label}</button>)}
          </div>
          {kind === 'image' ? (
            <div className="bnr-pics" aria-label="Forhåndsvisning av bildet">
              {(pic || editing) ? <>
                <div><div className="bnr-pic full">{pic ? <img src={pic.data} alt="" /> : <StaffPicture id={editing!.id} className="" />}</div><div className="bnr-pic-cap">Hele skjermen</div></div>
                <div><div className="bnr-pic half">{pic ? <img src={pic.data} alt="" /> : <StaffPicture id={editing!.id} className="" />}</div><div className="bnr-pic-cap">Delt visning (ved siden av bønnetidene)</div></div>
              </> : <p className="note">Velg et bilde, så ser du det her i begge størrelsene en skjerm kan vise det.</p>}
            </div>
          ) : (
            <div className={'bnr-wrap' + (shape === 'split' ? ' split' : shape === 'phone' ? ' phone' : '')} aria-label="Forhåndsvisning av banneret">{face}</div>
          )}
          <p className="note" style={{ marginTop: 8 }}>{SHAPES.find((s) => s.id === shape)?.note} {kind !== 'image' && 'Slik ser det ut på norsk; engelsk og urdu lages av teksten.'}</p>
          <Msg text={warn.text} tone={warn.tone} />
        </div>
      </div>
    </div>
  );
}
